import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { useBudgetStatus, useCreateBudget, useDeleteBudget } from '../hooks/useOps';
import type { BudgetPeriodType, BudgetScope, BudgetState, BudgetStatusRow, ExpenseCategory } from '../api/types';
import { formatIDR, humanizeStatus } from '../lib/format';

const CATEGORIES: ExpenseCategory[] = ['infra', 'saas', 'ai_api', 'contractor', 'office', 'tax', 'other'];

const STATE_COLOR: Record<BudgetState, 'success' | 'warning' | 'error'> = {
  ok: 'success',
  warn: 'warning',
  over: 'error',
};

function currentMasa(): string {
  return new Date().toISOString().slice(0, 7);
}

export function BudgetsPage() {
  const [period, setPeriod] = useState(currentMasa());
  const { data, isLoading, error } = useBudgetStatus(period || undefined);
  const createBudget = useCreateBudget();
  const deleteBudget = useDeleteBudget();

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<BudgetScope>('category');
  const [key, setKey] = useState<string>('ai_api');
  const [periodType, setPeriodType] = useState<BudgetPeriodType>('month');
  const [budgetPeriod, setBudgetPeriod] = useState(currentMasa());
  const [amount, setAmount] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  const submit = async () => {
    try {
      await createBudget.mutateAsync({
        scope,
        key: scope === 'company' ? undefined : key,
        periodType,
        period: periodType === 'month' ? budgetPeriod : undefined,
        amount,
      });
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this budget?')) return;
    await deleteBudget.mutateAsync(id);
  };

  const columns: GridColDef<BudgetStatusRow>[] = [
    { field: 'scope', headerName: 'Scope', width: 110, valueGetter: (v) => humanizeStatus(v as string) },
    {
      field: 'key',
      headerName: 'Target',
      flex: 1,
      minWidth: 160,
      valueGetter: (v, row) => (row.scope === 'company' ? 'Whole company' : humanizeStatus(String(v ?? ''))),
    },
    {
      field: 'period',
      headerName: 'Period',
      width: 110,
      valueGetter: (v, row) => (row.periodType === 'total' ? 'All-time' : (v as string)),
    },
    {
      field: 'budget',
      headerName: 'Budget',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
    {
      field: 'actual',
      headerName: 'Actual',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
    {
      field: 'pct',
      headerName: 'Used',
      width: 180,
      sortable: false,
      renderCell: (p) => {
        const pct = Math.min(Number(p.value) * 100, 100);
        return (
          <Stack direction="row" alignItems="center" gap={1} width="100%">
            <LinearProgress
              variant="determinate"
              value={pct}
              color={STATE_COLOR[p.row.state]}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <span>{(Number(p.value) * 100).toFixed(0)}%</span>
          </Stack>
        );
      },
    },
    {
      field: 'state',
      headerName: 'State',
      width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value} color={STATE_COLOR[p.value as BudgetState]} variant="outlined" />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={() => remove(p.row.id)} aria-label="delete">
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Budgets"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormError(null); setOpen(true); }}>
            New budget
          </Button>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message}</Alert>}

      <Stack direction="row" gap={2} mb={2}>
        <TextField
          label="Evaluate for masa"
          type="month"
          size="small"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="All-time budgets always included"
        />
      </Stack>

      <Paper variant="outlined" sx={{ height: 560 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New budget</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField select label="Scope" value={scope} onChange={(e) => {
              const s = e.target.value as BudgetScope;
              setScope(s);
              if (s === 'category') setKey('ai_api');
              else setKey('');
            }}>
              <MenuItem value="category">Category</MenuItem>
              <MenuItem value="project">Project</MenuItem>
              <MenuItem value="company">Company (burn)</MenuItem>
            </TextField>
            {scope === 'category' && (
              <TextField select label="Category" value={key} onChange={(e) => setKey(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {humanizeStatus(c)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {scope === 'project' && (
              <TextField
                label="Project id (uuid)"
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
            )}
            <TextField
              select
              label="Period type"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as BudgetPeriodType)}
            >
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="total">All-time cap</MenuItem>
            </TextField>
            {periodType === 'month' && (
              <TextField
                label="Masa"
                type="month"
                value={budgetPeriod}
                onChange={(e) => setBudgetPeriod(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Budget amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={amount <= 0 || (scope !== 'company' && !key.trim()) || createBudget.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
